import { ReloadOutlined } from "@ant-design/icons"
import { Button, Skeleton, Typography } from "antd"
import { useTranslation } from "react-i18next"

import type { Notification } from "../types/notification.types.ts"
import { NotificationsList } from "./NotificationsList.tsx"

const { Text } = Typography

interface NotificationsContentProps {
  notifications: Notification[]
  hasNewNotification: boolean
  isModal?: boolean
  fullHeight?: boolean
  onMarkAsRead: (id: number | null) => void
  onCardClick: (notification: Notification) => void
  onArchive: (id: number | null) => void
  hasMore: boolean
  onLoadMore: () => void
  refetchNotifications: () => void
  fetching: boolean
}

export function NotificationsContent({
  notifications,
  hasNewNotification,
  isModal = false,
  fullHeight = false,
  onMarkAsRead,
  onCardClick,
  onArchive,
  hasMore,
  onLoadMore,
  refetchNotifications,
  fetching
}: NotificationsContentProps) {
  const { t } = useTranslation()

  // 91px = the two-line drawer header phones always render, plus body padding.
  const scrollStyle = fullHeight
    ? { flex: 1, minHeight: 0 }
    : { maxHeight: isModal ? "75vh" : 400 }
  const listHeight = fullHeight
    ? "calc(100dvh - 91px)"
    : isModal
      ? "70vh"
      : 360

  return (
    <div
      className="relative"
      style={{
        width: isModal ? "100%" : 350,
        maxHeight: "100%",
        ...(fullHeight && {
          height: "100%",
          display: "flex",
          flexDirection: "column" as const
        })
      }}
    >
      {hasNewNotification && (
        <Button
          type="primary"
          size="small"
          icon={<ReloadOutlined />}
          className="absolute top-2 left-1/2 -translate-x-1/2 z-10 shadow-lg"
          onClick={refetchNotifications}
        >
          {t("notifications.bell.newNotification")}
        </Button>
      )}

      <div
        className="tailwind-scroll overflow-auto p-1 -m-1"
        style={scrollStyle}
      >
        {fetching ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : notifications.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center" }}>
            <Text type="secondary">{t("notifications.bell.empty")}</Text>
          </div>
        ) : (
          <NotificationsList
            notifications={notifications}
            onMarkAsRead={onMarkAsRead}
            onCardClick={onCardClick}
            onArchive={onArchive}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
            height={listHeight}
          />
        )}
      </div>
    </div>
  )
}
